import React, { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { Tag } from 'primereact/tag';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { useLazyGetRefundOrderDetailQuery, useUpdateRefundDraftMutation, useSubmitRefundForApprovalMutation, useProcessRefundApprovalMutation } from '../../../../api/endpoints/vehicleRefundOrder';
import { useGetLookUpDropdownListQuery } from '../../../../api/endpoints/lookup';
import { getRefundStatusSeverity, REFUND_APPROVAL_ACTION } from '../../vehiclesForSale/utils/statusBadges';
import { toastService } from '../../../../utils/toastService';
import RejectRefundDialog from './RejectRefundDialog';

const RefundOrderDetailDialog = ({ visible, order, onHide, onActionComplete }) => {
    const { th, getCurrentLanguageId, getCurrentLocale } = useLanguage();
    const languageId = getCurrentLanguageId();

    const [fetchDetail, { data: detail, isFetching: detailLoading }] = useLazyGetRefundOrderDetailQuery();
    const [updateDraft, { isLoading: isUpdating }] = useUpdateRefundDraftMutation();
    const [submitForApproval, { isLoading: isSubmitting }] = useSubmitRefundForApprovalMutation();
    const [processApproval, { isLoading: isProcessing }] = useProcessRefundApprovalMutation();

    const { data: refundReasons = [] } = useGetLookUpDropdownListQuery(
        { GroupCode: 'IADE_SIPARIS_NEDENI', LanguageId: languageId },
        { skip: !visible }
    );

    const [refundReasonCode, setRefundReasonCode] = useState('');
    const [note, setNote] = useState('');
    const [validationErrors, setValidationErrors] = useState({});
    const [rejectDialogVisible, setRejectDialogVisible] = useState(false);

    const isActionLoading = isUpdating || isSubmitting || isProcessing;

    useEffect(() => {
        if (visible && order?.id) {
            fetchDetail({ refundOrderId: order.id, languageId });
        }
    }, [visible, order?.id, languageId]);

    useEffect(() => {
        if (detail) {
            setRefundReasonCode(detail.refundReasonCode || '');
            setNote(detail.note || '');
            setValidationErrors({});
        }
    }, [detail]);

    const formatDate = (value) => {
        if (!value) return '-';
        try {
            return new Date(value).toLocaleDateString(getCurrentLocale(), { day: '2-digit', month: '2-digit', year: 'numeric' });
        } catch {
            return value;
        }
    };

    const reasonOptions = refundReasons.map((r) => ({
        label: r.translatedText || r.text || r.description || r.name || r.code,
        value: r.code
    }));

    const getReasonLabel = (code) => {
        if (!code) return '-';
        const found = reasonOptions.find((r) => r.value === code);
        return found?.label || code;
    };

    const validate = () => {
        const errors = {};
        if (!refundReasonCode) errors.refundReasonCode = th('vehicleRefundOrders.validation.refundReasonRequired');
        if (!note?.trim()) errors.note = th('vehicleRefundOrders.validation.noteRequired');
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSave = async () => {
        if (!detail?.id) return;
        try {
            const res = await updateDraft({
                refundOrderId: detail.id,
                refundReasonCode,
                note: note?.trim() || '',
                languageId
            }).unwrap();
            if (res?.isSuccessful || res?.isSuccess) {
                toastService.success(res.message || th('vehicleRefundOrders.messages.draftUpdated'));
                onActionComplete?.();
            } else {
                toastService.error(res?.errorMessage || th('vehicleRefundOrders.messages.operationFailed'));
            }
        } catch (err) {
            toastService.error(err?.data?.errorMessage || err?.errorMessage || th('vehicleRefundOrders.messages.operationFailed'));
        }
    };

    const handleSubmit = async () => {
        if (!validate() || !detail?.id) return;
        try {
            const res = await submitForApproval({
                refundOrderId: detail.id,
                refundReasonCode,
                note: note?.trim() || '',
                languageId
            }).unwrap();
            if (res?.isSuccessful || res?.isSuccess) {
                toastService.success(res.message || th('vehicleRefundOrders.messages.submittedForApproval'));
                onActionComplete?.();
                onHide();
            } else {
                toastService.error(res?.errorMessage || th('vehicleRefundOrders.messages.operationFailed'));
            }
        } catch (err) {
            toastService.error(err?.data?.errorMessage || err?.errorMessage || th('vehicleRefundOrders.messages.operationFailed'));
        }
    };

    const handleApprove = async () => {
        if (!detail?.id) return;
        try {
            const res = await processApproval({
                refundOrderId: detail.id,
                action: REFUND_APPROVAL_ACTION.APPROVE,
                languageId
            }).unwrap();
            if (res?.isSuccessful || res?.isSuccess) {
                toastService.success(res.message || th('vehicleRefundOrders.messages.approved'));
                onActionComplete?.();
                onHide();
            } else {
                toastService.error(res?.errorMessage || th('vehicleRefundOrders.messages.operationFailed'));
            }
        } catch (err) {
            toastService.error(err?.data?.errorMessage || err?.errorMessage || th('vehicleRefundOrders.messages.operationFailed'));
        }
    };

    const handleRejectConfirm = async (rejectReason) => {
        if (!detail?.id) return;
        try {
            const res = await processApproval({
                refundOrderId: detail.id,
                action: REFUND_APPROVAL_ACTION.REJECT,
                rejectReason,
                languageId
            }).unwrap();
            if (res?.isSuccessful || res?.isSuccess) {
                toastService.success(res.message || th('vehicleRefundOrders.messages.rejected'));
                setRejectDialogVisible(false);
                onActionComplete?.();
                onHide();
            } else {
                toastService.error(res?.errorMessage || th('vehicleRefundOrders.messages.operationFailed'));
            }
        } catch (err) {
            toastService.error(err?.data?.errorMessage || err?.errorMessage || th('vehicleRefundOrders.messages.operationFailed'));
        }
    };

    const canEdit = detail?.canEdit;
    const canSubmit = detail?.canSubmit;
    const canApprove = detail?.canApprove;
    const canReject = detail?.canReject;

    const renderFooter = () => {
        if (canEdit) {
            return (
                <div className="flex justify-content-end gap-2">
                    <Button label={th('vehicleRefundOrders.buttons.cancel')} icon="pi pi-times" outlined onClick={onHide} disabled={isActionLoading} />
                    <Button label={th('vehicleRefundOrders.buttons.save')} icon="pi pi-save" onClick={handleSave} loading={isUpdating} disabled={isSubmitting} />
                    {canSubmit && (
                        <Button
                            label={th('vehicleRefundOrders.buttons.submitForApproval')}
                            icon="pi pi-check"
                            className="p-button-success"
                            onClick={handleSubmit}
                            loading={isSubmitting}
                            disabled={isUpdating}
                        />
                    )}
                </div>
            );
        }

        if (canApprove || canReject) {
            return (
                <div className="flex justify-content-end gap-2">
                    <Button label={th('vehicleRefundOrders.buttons.close')} icon="pi pi-times" outlined onClick={onHide} disabled={isActionLoading} />
                    {canReject && (
                        <Button
                            label={th('vehicleRefundOrders.buttons.reject')}
                            icon="pi pi-times-circle"
                            className="p-button-danger"
                            onClick={() => setRejectDialogVisible(true)}
                            disabled={isActionLoading}
                        />
                    )}
                    {canApprove && (
                        <Button
                            label={th('vehicleRefundOrders.buttons.approve')}
                            icon="pi pi-check"
                            className="p-button-success"
                            onClick={handleApprove}
                            loading={isProcessing}
                        />
                    )}
                </div>
            );
        }

        return (
            <div className="flex justify-content-end gap-2">
                <Button label={th('vehicleRefundOrders.buttons.close')} icon="pi pi-times" outlined onClick={onHide} />
            </div>
        );
    };

    const readonlyField = (label, value) => (
        <div className="col-12 md:col-6">
            <div className="field">
                <label className="block mb-1 text-sm font-medium">{label}</label>
                <InputText value={value ?? '-'} readOnly className="w-full" />
            </div>
        </div>
    );

    return (
        <>
            <Dialog
                header={`${th('vehicleRefundOrders.detail.title')} #${detail?.dmsRefundNumber || order?.dmsRefundNumber || ''}`}
                visible={visible}
                onHide={onHide}
                style={{ width: '70vw', maxWidth: '800px' }}
                footer={renderFooter()}
                modal
                maximizable
            >
                {detailLoading && !detail ? (
                    <div className="flex justify-content-center p-5">
                        <ProgressSpinner />
                    </div>
                ) : detail ? (
                    <div className="grid">
                        <div className="col-12">
                            <h5 className="mt-2">{th('vehicleRefundOrders.detail.headerInfo')}</h5>
                        </div>

                        {detail.statusText && (
                            <div className="col-12">
                                <div className="field">
                                    <label className="block mb-1 text-sm font-medium">{th('vehicleRefundOrders.columns.status')}</label>
                                    <Tag value={detail.statusText} severity={getRefundStatusSeverity(detail.status)} />
                                </div>
                            </div>
                        )}

                        {readonlyField(th('vehicleRefundOrders.detail.dmsRefundNumber'), detail.dmsRefundNumber)}
                        {readonlyField(th('vehicleRefundOrders.detail.dmsOrderNumber'), detail.dmsOrderNumber)}
                        {readonlyField(th('vehicleRefundOrders.detail.customerNumber'), detail.kunnr)}
                        {readonlyField(th('vehicleRefundOrders.detail.chassisNo'), detail.chassisNo)}
                        {readonlyField(th('vehicleRefundOrders.detail.matnr'), detail.matnr)}
                        {readonlyField(th('vehicleRefundOrders.detail.maktx'), detail.maktx)}
                        {readonlyField(th('vehicleRefundOrders.detail.brand'), detail.brand)}
                        {readonlyField(th('vehicleRefundOrders.detail.model'), detail.model)}
                        {readonlyField(th('vehicleRefundOrders.detail.partyNumber'), detail.partyNumber)}
                        {readonlyField(th('vehicleRefundOrders.detail.currency'), detail.currency)}
                        {readonlyField(th('vehicleRefundOrders.detail.meins'), detail.meins)}
                        {readonlyField(th('vehicleRefundOrders.detail.quantity'), String(detail.quantity ?? ''))}
                        {readonlyField(th('vehicleRefundOrders.detail.deliveryDate'), formatDate(detail.deliveryDate))}
                        {readonlyField(th('vehicleRefundOrders.detail.bosBranchCode'), detail.bosBranchCode)}

                        <div className="col-12">
                            <h5 className="mt-2">{th('vehicleRefundOrders.detail.refundInfo')}</h5>
                        </div>

                        {canEdit ? (
                            <>
                                <div className="col-12 md:col-6">
                                    <div className="field">
                                        <label className="block mb-1 text-sm font-medium">
                                            {th('vehicleRefundOrders.detail.refundReason')} *
                                        </label>
                                        <Dropdown
                                            value={refundReasonCode}
                                            options={reasonOptions}
                                            optionLabel="label"
                                            optionValue="value"
                                            onChange={(e) => {
                                                setRefundReasonCode(e.value);
                                                setValidationErrors((prev) => ({ ...prev, refundReasonCode: undefined }));
                                            }}
                                            placeholder={th('vehicleRefundOrders.createDialog.refundReasonPlaceholder')}
                                            className={`w-full ${validationErrors.refundReasonCode ? 'p-invalid' : ''}`}
                                            filter
                                            showClear
                                        />
                                        {validationErrors.refundReasonCode && (
                                            <small className="p-error block mt-1">{validationErrors.refundReasonCode}</small>
                                        )}
                                    </div>
                                </div>
                                <div className="col-12">
                                    <div className="field">
                                        <label className="block mb-1 text-sm font-medium">
                                            {th('vehicleRefundOrders.detail.note')} *
                                        </label>
                                        <InputTextarea
                                            value={note}
                                            onChange={(e) => {
                                                setNote(e.target.value);
                                                setValidationErrors((prev) => ({ ...prev, note: undefined }));
                                            }}
                                            rows={4}
                                            className={`w-full ${validationErrors.note ? 'p-invalid' : ''}`}
                                            placeholder={th('vehicleRefundOrders.createDialog.notePlaceholder')}
                                            autoResize
                                        />
                                        {validationErrors.note && (
                                            <small className="p-error block mt-1">{validationErrors.note}</small>
                                        )}
                                    </div>
                                </div>
                            </>
                        ) : (
                            <>
                                {readonlyField(th('vehicleRefundOrders.detail.refundReason'), getReasonLabel(detail.refundReasonCode))}
                                <div className="col-12">
                                    <div className="field">
                                        <label className="block mb-1 text-sm font-medium">{th('vehicleRefundOrders.detail.note')}</label>
                                        <InputTextarea value={detail.note || '-'} readOnly rows={3} className="w-full" autoResize />
                                    </div>
                                </div>
                            </>
                        )}

                        {(detail.approvedName || detail.rejectedName) && (
                            <>
                                <div className="col-12">
                                    <h5 className="mt-2">{th('vehicleRefundOrders.detail.processInfo')}</h5>
                                </div>
                                {detail.approvedName && readonlyField(th('vehicleRefundOrders.detail.approvedBy'), detail.approvedName)}
                                {detail.approvedDate && readonlyField(th('vehicleRefundOrders.detail.approvedDate'), formatDate(detail.approvedDate))}
                                {detail.rejectedName && readonlyField(th('vehicleRefundOrders.detail.rejectedBy'), detail.rejectedName)}
                                {detail.rejectedDate && readonlyField(th('vehicleRefundOrders.detail.rejectedDate'), formatDate(detail.rejectedDate))}
                            </>
                        )}

                        {detail.rejectReason && (
                            <div className="col-12">
                                <div className="field">
                                    <label className="block mb-1 text-sm font-medium">{th('vehicleRefundOrders.detail.rejectReason')}</label>
                                    <InputTextarea value={detail.rejectReason} readOnly rows={3} className="w-full" autoResize />
                                </div>
                            </div>
                        )}

                        <div className="col-12">
                            <h5 className="mt-2">{th('vehicleRefundOrders.detail.auditInfo')}</h5>
                        </div>
                        {readonlyField(th('vehicleRefundOrders.detail.createdDate'), formatDate(detail.createdDate))}
                        {readonlyField(th('vehicleRefundOrders.detail.createdName'), detail.createdName)}
                        {detail.modifiedDate && readonlyField(th('vehicleRefundOrders.detail.modifiedDate'), formatDate(detail.modifiedDate))}
                        {detail.modifiedName && readonlyField(th('vehicleRefundOrders.detail.modifiedName'), detail.modifiedName)}
                    </div>
                ) : null}
            </Dialog>

            <RejectRefundDialog
                visible={rejectDialogVisible}
                onHide={() => setRejectDialogVisible(false)}
                onReject={handleRejectConfirm}
                isRejecting={isProcessing}
            />
        </>
    );
};

export default RefundOrderDetailDialog;
